import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { useState } from "react";
import { fetchAiCredits } from "@store/globalSlice";
import { Status } from "@models/common";
import { message } from "antd";
import { t } from "i18next";
import { useMembershipLevel } from "@hooks/membership";
import { getPreviewVideoCredit, recreateVideoCreation, type UserAssetV2Record } from "@apis/http/api";
import { VideoRecreationRequestInternalModelId } from "@apis/generated/openapi";
import { isNil } from "@kalodata/tools";
import { notifyGeneratedVideoAssetsChanged } from "../hooks/useGeneratedVideoAssets";
import { isVideoRemake } from "@layouts/GlobalVideoDetailModal/helpers";

const retryCostCache = new Map<string, number>();

/** 卡片是否为视频复刻（video remake）任务。复刻任务不支持「重新生成」与「Report Issue」。 */
export const useIsVideoRemake = (record?: UserAssetV2Record) => isVideoRemake(null, record);

type VideoJobContext = {
  aspectRatio?: string;
  duration?: number;
  quantity?: number;
  resolution?: string;
};

function buildCostCacheKey(ctx: VideoJobContext | undefined, internalModelId: string) {
  return `${ctx?.aspectRatio}_${ctx?.duration}_${ctx?.quantity || 1}_${ctx?.resolution}_${internalModelId}`;
}

export const useVideoGenerateCard = (record: UserAssetV2Record, onRefresh?: () => void) => {
  const [retryCost, setRetryCost] = useState<number | null>(null);
  const [retryStatus, setRetryStatus] = useState<Status>("idle");
  const { totalRemain } = useAppSelector((state) => state.globalReducer.creditInfo);
  const dispatch = useAppDispatch();
  const { isBasicUp } = useMembershipLevel();

  const context = record?.jobItem?.context as VideoJobContext | undefined;
  const internalModelId = record?.jobItem?.internalModelId || "";
  const { aspectRatio, duration, quantity, resolution } = context || {};
  const jobId = record?.jobItem?.jobId;

  const fetchRetryCost = async (): Promise<number> => {
    const cacheKey = buildCostCacheKey(context, internalModelId);
    const cached = retryCostCache.get(cacheKey);
    if (cached !== undefined) return cached;

    const res = await getPreviewVideoCredit({
      aspectRatio,
      duration,
      quantity,
      resolution,
      internalModelId,
    } as unknown as any);
    const cost: number = res || 0;
    retryCostCache.set(cacheKey, cost);
    return cost;
  };

  const handleRetryHoverChange = async (open: boolean) => {
    if (!open || retryCost !== null) return;
    if (isNil(internalModelId) || !internalModelId) {
      return;
    }
    try {
      const cost = await fetchRetryCost();
      setRetryCost(cost);
    } catch {
      setRetryCost(0);
    }
  };

  const handleVideoRecreation = async () => {
    if (!jobId || retryStatus === "loading") return;
    if (isNil(internalModelId) || !internalModelId) {
      message.error(t("This model is unavailable. Unable to regenerate."));
      return;
    }
    if (retryCost === null) {
      try {
        const cost = await fetchRetryCost();
        setRetryCost(cost);
      } catch {
        message.error(t("Failed to check credits"));
        return;
      }
    }

    if (retryCost && totalRemain < retryCost) return;

    setRetryStatus("loading");
    try {
      await recreateVideoCreation({ jobId, internalModelId: internalModelId as VideoRecreationRequestInternalModelId });
      setRetryStatus("succeeded");
      message.success(t("Retry submitted successfully"));
      notifyGeneratedVideoAssetsChanged();
      onRefresh?.();
      dispatch(fetchAiCredits());
    } catch {
      setRetryStatus("failed");
      message.error(t("Retry failed"));
    }
  };

  return {
    retryCost,
    totalRemain,
    isBasicUp,
    handleRetryHoverChange,
    handleVideoRecreation,
  };
};
